/**
 * Single source of truth for canonical `FamilyMemberRelationship` labels
 * (Phase F.1a / GEN-2997). Pairs with the canonical UPPER_CASE enum added in
 * Phase A (`design_docs/comparator_relationship_model_design.md` §3.2) and
 * mirrored into the SPA in Phase B.2 (`canonicalOrder.ts`).
 *
 * F.1a ships these labels for the new dormant `<ComparatorCard>` and
 * `<ComparatorModal>`. F.1b consumes them in OrderInProgress + Review.
 *
 * **Backward compatibility**: the helper `formatComparatorRelationshipLabel`
 * accepts both canonical UPPER_CASE values (preferred) and legacy
 * lowercase values (`bio_mother`, `bio_father`, `sibling`, `proband`,
 * `other`) so any pre-Phase-E canonical payload still renders correctly.
 */

import type { FamilyMemberRelationship } from './canonicalOrder'

/**
 * The 30 canonical UPPER_CASE literals from the design doc. Excludes the
 * five legacy lowercase strings (those are handled in
 * `formatComparatorRelationshipLabel` only).
 */
export type CanonicalFamilyMemberRelationship = Exclude<
  FamilyMemberRelationship,
  'proband' | 'bio_mother' | 'bio_father' | 'sibling' | 'other' | 'legal_guardian'
>

/**
 * Display labels for every canonical literal. New literals MUST add a
 * label here; the exhaustive `Record` type prevents silent drift.
 */
export const COMPARATOR_RELATIONSHIP_LABELS: Record<CanonicalFamilyMemberRelationship, string> = {
  PROBAND: 'Proband',
  MOTHER: 'Mother',
  FATHER: 'Father',
  LEGAL_GUARDIAN: 'Legal guardian',
  OTHER: 'Other',
  SIBLING: 'Sibling',
  TWIN_SIBLING: 'Twin sibling',
  MULTIPLE_BIRTH_SIBLING: 'Multiple-birth sibling',
  HALF_SIBLING_MATERNAL: 'Half-sibling (maternal)',
  HALF_SIBLING_PATERNAL: 'Half-sibling (paternal)',
  GRANDMOTHER_MATERNAL: 'Grandmother (maternal)',
  GRANDFATHER_MATERNAL: 'Grandfather (maternal)',
  GRANDMOTHER_PATERNAL: 'Grandmother (paternal)',
  GRANDFATHER_PATERNAL: 'Grandfather (paternal)',
  GREAT_GRANDMOTHER_MATERNAL: 'Great-grandmother (maternal)',
  GREAT_GRANDFATHER_MATERNAL: 'Great-grandfather (maternal)',
  GREAT_GRANDMOTHER_PATERNAL: 'Great-grandmother (paternal)',
  GREAT_GRANDFATHER_PATERNAL: 'Great-grandfather (paternal)',
  AUNT_MATERNAL: 'Aunt (maternal)',
  UNCLE_MATERNAL: 'Uncle (maternal)',
  AUNT_PATERNAL: 'Aunt (paternal)',
  UNCLE_PATERNAL: 'Uncle (paternal)',
  COUSIN_MATERNAL: 'Cousin (maternal)',
  COUSIN_PATERNAL: 'Cousin (paternal)',
  NIECE: 'Niece',
  NEPHEW: 'Nephew',
  CHILD: 'Child',
  GRANDCHILD: 'Grandchild',
}

/**
 * Legacy lowercase → canonical mapping for read tolerance (archive payloads
 * + pre-Phase-E DB state). Mirrors `imdb_database` Phase E rewrite.
 */
const LEGACY_TO_CANONICAL: Record<string, CanonicalFamilyMemberRelationship> = {
  proband: 'PROBAND',
  bio_mother: 'MOTHER',
  bio_father: 'FATHER',
  sibling: 'SIBLING',
  other: 'OTHER',
  legal_guardian: 'LEGAL_GUARDIAN',
}

/**
 * Relationships the provider-api actually accepts on submit. The API validates
 * `relationship` against a strict literal set; anything outside it fails with a
 * pydantic `literal_error` (e.g. sending `OTHER` → "Input should be 'Proband',
 * 'Mother', 'Father', … or 'Maternal Grandmother'"). We therefore restrict the
 * relationship *pickers* to this intersection of the canonical SPA vocabulary
 * and the API's allowlist.
 *
 * The API list also includes `Daughter` / `Son`, which have no corresponding
 * canonical SPA literal (the SPA models descendants as `CHILD` / `GRANDCHILD`,
 * which the API does NOT accept), so those are intentionally omitted here until
 * the vocabularies are reconciled.
 *
 * NOTE: this only constrains what a user can *select*. Reading/formatting stays
 * exhaustive via {@link COMPARATOR_RELATIONSHIP_LABELS} /
 * {@link formatComparatorRelationshipLabel} so any already-stored extended
 * relationship still renders.
 */
export const API_SUPPORTED_RELATIONSHIP_VALUES: readonly CanonicalFamilyMemberRelationship[] = [
  'PROBAND',
  'MOTHER',
  'FATHER',
  'SIBLING',
  'AUNT_PATERNAL',
  'UNCLE_PATERNAL',
  'GRANDFATHER_PATERNAL',
  'GRANDMOTHER_PATERNAL',
  'AUNT_MATERNAL',
  'UNCLE_MATERNAL',
  'GRANDFATHER_MATERNAL',
  'GRANDMOTHER_MATERNAL',
]

export function isApiSupportedRelationship(
  value: CanonicalFamilyMemberRelationship | string | null | undefined
): value is CanonicalFamilyMemberRelationship {
  if (value == null) return false
  return (API_SUPPORTED_RELATIONSHIP_VALUES as readonly string[]).includes(String(value).trim())
}

/**
 * Pre-sorted, picker-friendly options. Order intentionally puts the most
 * common picks first (PROBAND, MOTHER, FATHER) then sorts the rest
 * alphabetically by label. Restricted to {@link API_SUPPORTED_RELATIONSHIP_VALUES}
 * so a user can never select a relationship the provider-api will reject on
 * submit. Use in `<select>` or `RadioOption`.
 */
export const COMPARATOR_RELATIONSHIP_OPTIONS: ReadonlyArray<{
  value: CanonicalFamilyMemberRelationship
  label: string
}> = (() => {
  const priority: CanonicalFamilyMemberRelationship[] = ['PROBAND', 'MOTHER', 'FATHER']
  const supported = API_SUPPORTED_RELATIONSHIP_VALUES
  const rest = supported
    .filter((v) => !priority.includes(v))
    .sort((a, b) => COMPARATOR_RELATIONSHIP_LABELS[a].localeCompare(COMPARATOR_RELATIONSHIP_LABELS[b]))
  return [...priority.filter((v) => supported.includes(v)), ...rest].map((value) => ({
    value,
    label: COMPARATOR_RELATIONSHIP_LABELS[value],
  }))
})()

/**
 * Picker options for the "Add family member" flow (GEN-3152 Phase 2).
 * Excludes PROBAND (the patient) and MOTHER / FATHER — those are captured by
 * their dedicated parent cards and would collide with the parent slot sync.
 * Inherits the {@link API_SUPPORTED_RELATIONSHIP_VALUES} restriction from
 * {@link COMPARATOR_RELATIONSHIP_OPTIONS}, so the add flow can only append a
 * comparator the provider-api will accept and never stomps the parent surface.
 */
export const NON_PARENT_COMPARATOR_RELATIONSHIP_OPTIONS: ReadonlyArray<{
  value: CanonicalFamilyMemberRelationship
  label: string
}> = COMPARATOR_RELATIONSHIP_OPTIONS.filter(
  (o) => o.value !== 'PROBAND' && o.value !== 'MOTHER' && o.value !== 'FATHER'
)

/**
 * Picker options for the unified "Add family member" flow (R5 sequencing
 * decoupling). Includes MOTHER / FATHER so a biological parent can be added
 * directly as a comparator — independent of the sequencing test type / tier —
 * and the order tier is derived from `comparators[]`. Excludes PROBAND (the
 * patient) and inherits the {@link API_SUPPORTED_RELATIONSHIP_VALUES}
 * restriction from {@link COMPARATOR_RELATIONSHIP_OPTIONS}. Parent rows still
 * sync to the legacy `mother` / `father` slots via the comparator write
 * adapter, so downstream consumers stay consistent.
 */
export const ADD_FAMILY_MEMBER_RELATIONSHIP_OPTIONS: ReadonlyArray<{
  value: CanonicalFamilyMemberRelationship
  label: string
}> = COMPARATOR_RELATIONSHIP_OPTIONS.filter((o) => o.value !== 'PROBAND')

/**
 * Relationships an `inflection_test` order may pick: only the two biological
 * parents. `inflection_test` (LSMC / SeqOne) fulfillment supports proband +
 * parent trio/duo comparators only, so the picker is narrowed to MOTHER /
 * FATHER. `rcigm` (and the default) keep the full option list. Reading /
 * formatting stays exhaustive via {@link formatComparatorRelationshipLabel},
 * so already-stored extended relationships still render.
 */
const INFLECTION_TEST_RELATIONSHIP_VALUES: readonly CanonicalFamilyMemberRelationship[] = ['MOTHER', 'FATHER']

/**
 * Restrict a relationship-picker option list by fulfillment strategy. For
 * `inflection_test`, returns only the MOTHER / FATHER entries present in
 * `baseOptions` (order preserved). For every other strategy (including the
 * `rcigm` default) `baseOptions` is returned unchanged. This only constrains
 * what a user can select; it never rewrites stored data.
 */
export function relationshipOptionsForStrategy<T extends { value: CanonicalFamilyMemberRelationship; label: string }>(
  strategy: 'rcigm' | 'inflection_test' | undefined,
  baseOptions: ReadonlyArray<T>
): ReadonlyArray<T> {
  if (strategy !== 'inflection_test') {
    return baseOptions
  }
  return baseOptions.filter((o) => INFLECTION_TEST_RELATIONSHIP_VALUES.includes(o.value))
}

/**
 * Resolve a stored relationship literal (canonical or legacy) to a human
 * label. Returns the raw input when no match is found so debugging stays
 * possible without throwing for unexpected data.
 */
export function formatComparatorRelationshipLabel(value: FamilyMemberRelationship | string | null | undefined): string {
  if (value == null) return ''
  const raw = String(value).trim()
  if (!raw) return ''
  if (raw in COMPARATOR_RELATIONSHIP_LABELS) {
    return COMPARATOR_RELATIONSHIP_LABELS[raw as CanonicalFamilyMemberRelationship]
  }
  const canonical = LEGACY_TO_CANONICAL[raw]
  if (canonical) {
    return COMPARATOR_RELATIONSHIP_LABELS[canonical]
  }
  return raw
}

/**
 * True when the relationship is part of a multiple-birth gestation set
 * (twins or higher-order multiples). Used by `<ComparatorModal>` to
 * conditionally reveal the zygosity / birth-order sub-block.
 */
export function isMultipleBirthRelationship(value: FamilyMemberRelationship | string | null | undefined): boolean {
  if (value == null) return false
  const raw = String(value).trim()
  return raw === 'TWIN_SIBLING' || raw === 'MULTIPLE_BIRTH_SIBLING'
}

/**
 * Relationships a patient can legitimately have more than one of. Everyone else
 * (MOTHER / FATHER / grandparents / …) is a 1-of-1 "unique" relationship. Aunts
 * / uncles are split maternal + paternal, and each side can still have multiple
 * siblings, so every variant is repeatable. Twins and multiple-birth siblings
 * are likewise repeatable.
 *
 * Canonical UPPER_CASE only — normalize legacy lowercase before checking via
 * {@link isUniqueComparatorRelationship}, which handles both vocabularies.
 */
const REPEATABLE_COMPARATOR_RELATIONSHIPS: ReadonlySet<string> = new Set<string>([
  'SIBLING',
  'TWIN_SIBLING',
  'MULTIPLE_BIRTH_SIBLING',
  'AUNT_MATERNAL',
  'AUNT_PATERNAL',
  'UNCLE_MATERNAL',
  'UNCLE_PATERNAL',
])

/**
 * Whether a relationship is "unique" — i.e. a patient can have at most one
 * family member with it (MOTHER, FATHER, grandparents, cousins, …). Repeatable
 * relationships (siblings, twins, multiple-birth siblings, aunts / uncles) are
 * NOT unique. Accepts canonical UPPER_CASE or legacy lowercase values (the
 * legacy tokens are normalized first). Empty / unknown input is treated as not
 * unique so callers never over-eagerly assume a single-slot relationship.
 *
 * Shared by the "Add Comparator" relationship picker (to filter out an
 * already-used unique relationship) and the guardian → comparator demographic
 * pre-fill (which only copies when the relationship uniquely identifies one
 * family member).
 */
export function isUniqueComparatorRelationship(value: FamilyMemberRelationship | string | null | undefined): boolean {
  if (value == null) return false
  const raw = String(value).trim()
  if (!raw) return false
  const canonical = raw in COMPARATOR_RELATIONSHIP_LABELS ? raw : (LEGACY_TO_CANONICAL[raw] ?? raw)
  return !REPEATABLE_COMPARATOR_RELATIONSHIPS.has(canonical)
}
